import { IconClose, IconPlus } from "@synthesis/shared-icons";
import { Button, Input, NoticeBase, TextArea } from "@synthesis/shared-ui";
import { useEffect } from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { CreateApplicationFormField, CreateApplicationFormSection } from "../create-application-form-layout";
import { ADJACENT_LAND_DATA_AVAILABILITY_OPTIONS, type AdjacentObjectDataFormValues } from "../../create-application-page.lib";
import { AdjacentObjectDataRadioGroup } from "./create-application-adjacent-object-data-form-controls";

const CADASTRAL_NUMBER_PLACEHOLDER = "Например: 77:01:0001010:123 или 77:01:0001010";
const MAX_CADASTRAL_NUMBERS = 5;

type AdjacentObjectLandSectionProps = {
  isVisible: boolean;
};

export const AdjacentObjectLandSection = ({ isVisible }: AdjacentObjectLandSectionProps) => {
  const methods = useFormContext<AdjacentObjectDataFormValues>();
  const cadastralDataAvailability = useWatch({ name: "cadastralDataAvailability" });
  const cadastralNumbers = useWatch<AdjacentObjectDataFormValues, "cadastralNumbers">({ name: "cadastralNumbers" }) ?? [];

  const { setValue } = methods;

  useEffect(() => {
    if (isVisible) {
      return;
    }

    setValue("landStatus", "", { shouldDirty: false, shouldValidate: false });
    setValue("landLocationDescription", "", { shouldDirty: false, shouldValidate: false });
    setValue("cadastralNumbers", [], { shouldDirty: false, shouldValidate: false });
  }, [isVisible, setValue]);

  const handleCadastralDataAvailabilityChange = (value: string) => {
    if (value === "known") {
      methods.setValue("landStatus", "", { shouldValidate: true });
      methods.setValue("landLocationDescription", "", { shouldValidate: true });
      return;
    }

    methods.setValue("cadastralNumbers", [], { shouldValidate: true });
  };

  const appendCadastralNumber = () => {
    if (cadastralNumbers.length >= MAX_CADASTRAL_NUMBERS) {
      return;
    }

    methods.setValue("cadastralNumbers", [...(cadastralNumbers.length ? cadastralNumbers : [""]), ""], {
      shouldDirty: true,
      shouldValidate: false,
    });
  };

  const removeCadastralNumber = (index: number) => {
    methods.setValue(
      "cadastralNumbers",
      cadastralNumbers.filter((_, itemIndex) => itemIndex !== index),
      { shouldDirty: true, shouldValidate: true },
    );
  };

  if (!isVisible) {
    return null;
  }

  const cadastralNumberRows = Math.max(cadastralNumbers.length, 1);

  return (
    <CreateApplicationFormSection title="Земельный участок">
      <NoticeBase
        status="info"
        titleAppearance="plain"
        title="Укажите сведения о земельном участке, на котором планируется строительство пути."
        description="Если ваш проект охватывает более 5 земельных участков, достаточно указать их только на топографическом плане."
        className="max-w-none"
      />

      <CreateApplicationFormField label="У вас есть кадастровые данные об участке?" required>
        <AdjacentObjectDataRadioGroup
          name="cadastralDataAvailability"
          options={ADJACENT_LAND_DATA_AVAILABILITY_OPTIONS}
          horizontal
          onChange={handleCadastralDataAvailabilityChange}
        />
      </CreateApplicationFormField>

      {cadastralDataAvailability === "known" ? (
        <CreateApplicationFormField label="Кадастровый номер / квартал" className="max-w-[33rem]">
          <div className="flex flex-col gap-2">
            {Array.from({ length: cadastralNumberRows }, (_, index) => (
              <div key={`cadastral-number-${index}`} className="flex items-center gap-2">
                <Input name={`cadastralNumbers.${index}`} placeholder={CADASTRAL_NUMBER_PLACEHOLDER} className="flex-1" />
                {cadastralNumbers.length > 1 && (
                  <Button
                    type="button"
                    theme="text"
                    size="sm"
                    className="h-6 shrink-0 px-2 py-0.5"
                    leftIcon={<IconClose className="size-5" />}
                    onClick={() => removeCadastralNumber(index)}
                  >
                    Удалить
                  </Button>
                )}
              </div>
            ))}
          </div>
          {cadastralNumberRows < MAX_CADASTRAL_NUMBERS && (
            <Button type="button" theme="secondary" size="sm" leftIcon={<IconPlus className="size-5" />} onClick={appendCadastralNumber}>
              Добавить номер / квартал
            </Button>
          )}
        </CreateApplicationFormField>
      ) : (
        <CreateApplicationFormField label="Опишите местоположение участка своими словами">
          <TextArea
            name="landLocationDescription"
            rows={4}
            placeholder='Например: "в 500 м севернее деревни Иваново" или "территория промзоны на ул. Ленина"'
          />
        </CreateApplicationFormField>
      )}
    </CreateApplicationFormSection>
  );
};
